import Decimal from 'decimal.js'
import { t } from 'i18next'

import { AreaCode } from '@enechain/eposition-proto/bfftypes/v1/area_pb'
import {
  DealPosition,
  DeliveryHourType,
  DeliveryPeriodType,
  DeliveryType,
} from '@enechain/eposition-proto/types/v1/deal_pb'
import { stringUtils } from '@enechain/eposition-utils'

import { formatAreaAndHour, formatPeriod } from './helper'

type OrderSummaryText = {
  value: string
}

type SummaryTransaction = {
  position: DealPosition
  areaCode: AreaCode
  deliveryPeriodType: DeliveryPeriodType
  deliveryDateRange: {
    deliveryStartDate?: Date
    deliveryEndDate?: Date
  }
  deliveryHourType: DeliveryHourType
  volumeInKw: string
  unitPricePerKwh: string
  selfDeliveryType: DeliveryType
  counterpartyDeliveryType: DeliveryType
  isPhysicalFuture: boolean
  floatingIndexCode?: string | null
}

type Props = {
  isSpread: boolean
  base: SummaryTransaction
  exchange?: SummaryTransaction
}

/**
 * 取引情報からサマリーテキスト（呪文）を生成
 */
export function createOrderSummaryText({
  isSpread,
  base,
  exchange,
}: {
  isSpread: boolean
  base: SummaryTransaction
  exchange?: SummaryTransaction
}): OrderSummaryText {
  if (!shouldGenerateSummaryText({ isSpread, base, exchange })) {
    return { value: '' }
  }

  if (!isSpread) {
    const position =
      base.position === DealPosition.BID
        ? t('domain:shared.position.bid_short')
        : t('domain:shared.position.offer_short')
    const period = formatPeriod(
      {
        deliveryStartDate: base.deliveryDateRange.deliveryStartDate,
        deliveryEndDate: base.deliveryDateRange.deliveryEndDate,
      },
      base.deliveryPeriodType,
    )
    const area = formatAreaAndHour(base.areaCode, base.deliveryHourType)
    const deliveryTypeText = formatDeliveryTypeText(base)
    const deliveryTypeSuffix =
      deliveryTypeText !== '' ? ` ${deliveryTypeText}` : ''
    const fcaSuffix = stringUtils.isNotEmpty(base.floatingIndexCode)
      ? '+FCA'
      : ''
    return {
      value: `${position} ${area} ${period} ${base.unitPricePerKwh}${fcaSuffix}${t('common:unit.yen')} ${base.volumeInKw}MW${deliveryTypeSuffix}`,
    }
  }

  // スプレッドでexchangeがない場合は生成しない
  if (exchange === undefined) {
    return { value: '' }
  }

  // スプレッド取引の場合
  const position =
    base.position === DealPosition.BID
      ? t('domain:shared.position.bid_short')
      : t('domain:shared.position.offer_short')
  const basePeriod = formatPeriod(
    {
      deliveryStartDate: base.deliveryDateRange.deliveryStartDate,
      deliveryEndDate: base.deliveryDateRange.deliveryEndDate,
    },
    base.deliveryPeriodType,
  )
  const exchangePeriod = formatPeriod(
    {
      deliveryStartDate: exchange.deliveryDateRange.deliveryStartDate,
      deliveryEndDate: exchange.deliveryDateRange.deliveryEndDate,
    },
    exchange.deliveryPeriodType,
  )
  const baseArea = formatAreaAndHour(base.areaCode, base.deliveryHourType)
  const exchangeArea = formatAreaAndHour(
    exchange.areaCode,
    exchange.deliveryHourType,
  )
  const periodText =
    basePeriod === exchangePeriod
      ? basePeriod
      : `${basePeriod}/${exchangePeriod}`
  const areaText =
    baseArea === exchangeArea ? baseArea : `${baseArea}/${exchangeArea}`
  const volumeText =
    base.volumeInKw === exchange.volumeInKw
      ? `${base.volumeInKw}MW`
      : `${base.volumeInKw}MW/${exchange.volumeInKw}MW`
  const spreadValue = new Decimal(base.unitPricePerKwh).sub(
    new Decimal(exchange.unitPricePerKwh),
  )
  const spreadText = spreadValue.greaterThan(0)
    ? `+${spreadValue.toFixed(2)}`
    : spreadValue.toFixed(2)

  // スプレッド取引では基準側のdeliveryTypeを使用
  const deliveryTypeText = formatDeliveryTypeText(base)
  const deliveryTypeSuffix =
    deliveryTypeText !== '' ? ` ${deliveryTypeText}` : ''
  const fcaSuffix = stringUtils.isNotEmpty(base.floatingIndexCode) ? '+FCA' : ''

  return {
    value: `${position} ${areaText} ${periodText} ${spreadText}${fcaSuffix}${t('common:unit.yen')} ${volumeText}${deliveryTypeSuffix}`,
  }
}

function formatDeliveryTypeText(transaction: SummaryTransaction): string {
  if (transaction.isPhysicalFuture) {
    switch (transaction.counterpartyDeliveryType) {
      case DeliveryType.EEX:
        return 'on eClear(EEX)'
      default:
        return ''
    }
  } else {
    // 現現の場合は自社受渡区分を使用
    switch (transaction.selfDeliveryType) {
      case DeliveryType.BG:
      case DeliveryType.JEPX:
        return `on eClear(${t('domain:shared.physical_contract')})`
      default:
        return ''
    }
  }
}

function shouldGenerateSummaryText(props: Props): boolean {
  if (props.isSpread) {
    if (props.exchange === undefined) {
      return false
    }
    return (
      isSummaryTransactionValid(props.base) &&
      isSummaryTransactionValid(props.exchange)
    )
  }
  return isSummaryTransactionValid(props.base)
}

function isSummaryTransactionValid(leg: SummaryTransaction): boolean {
  if (leg.position === DealPosition.UNSPECIFIED) {
    return false
  }
  if (leg.areaCode === AreaCode.UNSPECIFIED) {
    return false
  }
  if (leg.deliveryPeriodType === DeliveryPeriodType.UNSPECIFIED) {
    return false
  }
  if (leg.deliveryDateRange.deliveryStartDate === undefined) {
    return false
  }
  if (leg.deliveryHourType === DeliveryHourType.UNSPECIFIED) {
    return false
  }
  if (stringUtils.isEmpty(leg.volumeInKw)) {
    return false
  }
  if (stringUtils.isEmpty(leg.unitPricePerKwh)) {
    return false
  }

  return true
}
